import React, { useState, memo } from 'react'
import { Task, TaskStatus } from '../../types/mockup'

interface TaskItemProps {
  task: Task
  isSelected?: boolean
  isCurrent?: boolean
  isNext?: boolean
  isDragging?: boolean
  onSelect?: (task: Task) => void
  onStatusChange?: (taskId: string, status: TaskStatus) => void
  onQuickAction?: (taskId: string, action: 'delay5' | 'delay10' | 'notify') => void
  onDragStart?: (e: React.DragEvent, task: Task) => void
  onDragEnd?: (e: React.DragEvent) => void
  onDragOver?: (e: React.DragEvent) => void
  onDrop?: (e: React.DragEvent, task: Task) => void
  showDependencies?: boolean
  relatedTasks?: Map<string, string>
}

export const TaskItem: React.FC<TaskItemProps> = memo(({
  task,
  isSelected = false,
  isCurrent = false,
  isNext = false,
  isDragging = false,
  onSelect,
  onStatusChange,
  onQuickAction,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  showDependencies = true,
  relatedTasks = new Map()
}) => {
  const [showActions, setShowActions] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)

  const getStatusConfig = (status: TaskStatus) => {
    const configs = {
      complete: {
        label: 'Complete',
        color: '#10b981',
        bgColor: 'rgba(16, 185, 129, 0.1)',
        borderColor: '#10b981',
        icon: '✓'
      },
      in_progress: {
        label: 'In Progress',
        color: '#f59e0b',
        bgColor: 'rgba(245, 158, 11, 0.1)',
        borderColor: '#f59e0b',
        icon: '●'
      },
      delayed: {
        label: 'Delayed',
        color: '#ef4444',
        bgColor: 'rgba(239, 68, 68, 0.1)',
        borderColor: '#ef4444',
        icon: '!'
      },
      at_risk: {
        label: 'At Risk',
        color: '#f97316',
        bgColor: 'rgba(249, 115, 22, 0.1)',
        borderColor: '#f97316',
        icon: '⚠'
      },
      pending: {
        label: 'Pending',
        color: '#6b7280',
        bgColor: 'rgba(107, 114, 128, 0.05)',
        borderColor: '#e5e7eb',
        icon: '○'
      }
    }
    return configs[status] || configs.pending
  }
  
  const getActionText = (): string | null => {
    if (task.isAnchor || task.status === 'complete') return null
    
    switch (task.status) {
      case 'pending':
        return 'Start Task'
      case 'in_progress':
        return 'Mark Done'
      case 'at_risk':
        return 'Resolve'
      case 'delayed':
        return 'Resume'
      default:
        return null
    }
  }
  
  const getActionTitle = (): string => {
    const actionText = getActionText()
    return actionText || statusConfig.label
  }

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours > 0) {
      return `${hours}h${mins > 0 ? ' ' + mins + 'm' : ''}`
    }
    return `${mins}m`
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSelect?.(task)
  }

  const handleStatusClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (task.isAnchor) return
    
    let newStatus: TaskStatus
    switch (task.status) {
      case 'pending':
        newStatus = 'in_progress'
        break
      case 'in_progress':
        newStatus = 'complete'
        break
      case 'at_risk':
        newStatus = 'complete'
        break
      case 'delayed':
        newStatus = 'in_progress'
        break
      default:
        return
    }
    
    onStatusChange?.(task.id, newStatus)
  }

  const handleQuickAction = (e: React.MouseEvent, action: 'delay5' | 'delay10' | 'notify') => {
    e.stopPropagation()
    onQuickAction?.(task.id, action)
    setShowActions(false)
  }

  const handleDragStart = (e: React.DragEvent) => {
    if (!task.isAnchor) {
      onDragStart?.(e, task)
    }
  }

  const handleDragEnter = (e: React.DragEvent) => {
    if (!task.isAnchor) {
      e.preventDefault()
      setIsDragOver(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    onDrop?.(e, task)
  }

  const statusConfig = getStatusConfig(task.status)
  
  const taskClasses = [
    'task-item',
    isSelected && 'task-selected',
    isCurrent && 'task-current',
    isNext && 'task-next',
    isDragging && 'task-dragging',
    task.isAnchor && 'task-anchor',
    isHovered && 'task-hovered',
    isDragOver && 'drag-over',
    task.status
  ].filter(Boolean).join(' ')

  const taskStyle: React.CSSProperties = {
    borderLeft: `4px solid ${statusConfig.borderColor}`,
    background: isSelected ? statusConfig.bgColor : undefined,
    opacity: isDragging ? 0.5 : 1,
    cursor: task.isAnchor ? 'not-allowed' : 'pointer'
  }

  return (
    <div
      className={taskClasses}
      style={taskStyle}
      data-task-id={task.id}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false)
        setShowActions(false)
      }}
      draggable={!task.isAnchor}
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={onDragOver}
      onDrop={handleDrop}
    >
      <div className="task-item-left">
        <div className="task-time-info">
          <div className="task-start-time">{formatTime(task.startTime)}</div>
          <div className="task-duration">{formatDuration(task.duration)}</div>
          {task.endTime && (
            <div className="task-end-time">→ {formatTime(task.endTime)}</div>
          )}
        </div>
      </div>

      <div className="task-item-center">
        <div className="task-header">
          {task.isAnchor && <span className="task-anchor-icon">🔒</span>}
          <span className="task-name">{task.name}</span>
          {task.priority === 'critical' && (
            <span className="task-priority-badge critical">Critical</span>
          )}
          {task.priority === 'high' && (
            <span className="task-priority-badge high">High</span>
          )}
          {task.delayMinutes && task.delayMinutes > 0 && (
            <span className="task-delay-badge">+{task.delayMinutes}min</span>
          )}
        </div>

        <div className="task-details">
          {task.supplier && (
            <div className="task-supplier">
              <span 
                className="supplier-chip"
                style={{
                  backgroundColor: task.supplier.color + '20',
                  color: task.supplier.color,
                  borderColor: task.supplier.color
                }}
              >
                {task.supplier.name}
              </span>
              {task.supplier.status === 'on_site' && (
                <span className="supplier-status">On Site</span>
              )}
            </div>
          )}

          {task.location && (
            <div className="task-location">
              <span className="location-icon">📍</span>
              <span className="location-text">{task.location}</span>
            </div>
          )}

          {task.assignedTo && task.assignedTo.length > 0 && (
            <div className="task-assigned">
              <span className="assigned-icon">👤</span>
              <span className="assigned-text">{task.assignedTo.join(', ')}</span>
            </div>
          )}

          {showDependencies && task.dependencies && task.dependencies.length > 0 && (
            <div className="task-dependencies">
              <span className="dep-label">
                <span className="dep-icon">⤴</span>
                Depends on:
              </span>
              {task.dependencies.map(depId => {
                const depName = relatedTasks.get(depId)
                return depName ? (
                  <span key={depId} className="dep-chip">{depName}</span>
                ) : null
              })}
            </div>
          )}

          {showDependencies && task.dependents && task.dependents.length > 0 && (
            <div className="task-dependents">
              <span className="dep-label">
                <span className="dep-icon">⤵</span>
                Blocks:
              </span>
              {task.dependents.map(depId => {
                const depName = relatedTasks.get(depId)
                return depName ? (
                  <span key={depId} className="dep-chip dependent">{depName}</span>
                ) : null
              })}
            </div>
          )}

          {task.notes && (
            <div className="task-notes">
              <span className="notes-icon">📝</span>
              <span className="notes-text">{task.notes}</span>
            </div>
          )}
        </div>
      </div>

      <div className="task-item-right">
        <div className="task-status-section">
          <button
            className={`task-status-btn status-${task.status} ${
              task.isAnchor ? 'disabled' : ''
            } ${
              isHovered && !task.isAnchor ? 'show-action' : ''
            }`}
            style={{ 
              backgroundColor: statusConfig.color,
              color: 'white',
              borderColor: statusConfig.color
            }}
            onClick={handleStatusClick}
            disabled={task.status === 'complete' || task.isAnchor}
            title={getActionTitle()}
          >
            <span className="status-icon">{statusConfig.icon}</span>
            <span className="status-label">
              {isHovered && !task.isAnchor && getActionText() ? getActionText() : statusConfig.label}
            </span>
          </button>
        </div>

        {isHovered && !task.isAnchor && (
          <div className="task-quick-actions">
            {task.status !== 'complete' && (
              <button
                className="quick-action at-risk"
                onClick={(e) => {
                  e.stopPropagation()
                  onStatusChange?.(task.id, 'at_risk')
                }}
                title="Mark At Risk"
              >
                ⚠
              </button>
            )}
            <button
              className="quick-action-btn"
              onClick={() => setShowActions(!showActions)}
            >
              ⋮
            </button>
            {showActions && (
              <div className="quick-actions-menu">
                <button
                  className="action-menu-item"
                  onClick={(e) => handleQuickAction(e, 'delay5')}
                >
                  +5 min
                </button>
                <button
                  className="action-menu-item"
                  onClick={(e) => handleQuickAction(e, 'delay10')}
                >
                  +10 min
                </button>
                <button
                  className="action-menu-item notify"
                  onClick={(e) => handleQuickAction(e, 'notify')}
                >
                  Notify Now
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {isCurrent && (
        <div className="task-current-indicator">
          <span className="current-badge">NOW</span>
        </div>
      )}

      {isNext && (
        <div className="task-next-indicator">
          <span className="next-badge">NEXT</span>
        </div>
      )}
    </div>
  )
})

TaskItem.displayName = 'TaskItem'

export default TaskItem